import { useMemo } from 'react';
import clsx from 'clsx';
import { useSimulationStore } from '@/state/simulationStore';
import { customerName, simTimeFull } from '@/utils/format';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import { deriveDepositStatus, DEPOSIT_STATUS_META } from '@/domain/depositStatus';
import type { CustomerRecord, Deposit } from '@/domain/types';

/**
 * Artists read "did they pay?", not the office's ledger distinction between a
 * paid deposit and one placed against a booking — so `placed` borrows the `paid`
 * chip wholesale (label, tone and icon). The Deposits ledger keeps both states.
 */
const ARTIST_STATUS_META: typeof DEPOSIT_STATUS_META = {
  ...DEPOSIT_STATUS_META,
  placed: DEPOSIT_STATUS_META.paid,
};

interface Props {
  deposits: Deposit[];
  customers: Record<string, CustomerRecord>;
}

export default function SelfBookStatusList({ deposits, customers }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const nowMs = new Date(clockIso).getTime();

  const rows = useMemo(
    () => [...deposits].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [deposits],
  );

  if (rows.length === 0) {
    return <p className="font-serif text-cream-soft text-sm">No links sent yet. Hold a slot above to create one.</p>;
  }

  return (
    <ul className="flex flex-col gap-2" data-testid="self-book-status-list">
      {rows.map((d) => {
        const status = deriveDepositStatus(d, nowMs);
        const meta = ARTIST_STATUS_META[status];
        const Icon = meta.icon;
        const isAwaitingClient = d.customerId === AWAITING_CLIENT;
        const cust = isAwaitingClient ? undefined : customers[d.customerId];
        const nameLabel = isAwaitingClient ? 'Awaiting client' : cust ? customerName(cust) : 'Client';
        return (
          <li
            key={d.id}
            className="rounded-lg border border-gold-dim/28 bg-ink-black px-3 py-2.5"
            data-testid={`self-book-status-${d.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-inter text-sm font-semibold text-cream">{nameLabel}</p>
                <p className="font-mono text-[10px] text-cream-faint mt-0.5">
                  {simTimeFull(d.createdAt)} · <span className="text-gold">{d.depositReference}</span>
                </p>
              </div>
              <span
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-inter font-bold uppercase tracking-widest',
                  meta.tone,
                )}
              >
                <Icon size={10} /> {meta.label}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
